import { memo, useEffect, useRef, useState } from 'react'
import {
  Container,
  CreateItem,
  Dropdown,
  DropdownItem,
  GroupTypeBadge,
  InputWrapper,
  SearchInput,
  Tag,
  TagRemoveBtn,
} from './PublisherPersonsGroupInput.style'

type Group = {
  id: string
  name: string
  isGlobal: boolean
}

type Props = {
  groups: Group[]
  selected: Group[]
  onChange: (groups: Group[]) => void
  onCreate: (name: string, onCreated: (group: Group) => void) => void
}

function normalize(str: string): string {
  return str
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
}

export const GroupTagInput = memo(function GroupTagInput({ groups, selected, onChange, onCreate }: Props) {
  const [inputValue, setInputValue] = useState('')
  const [isOpen, setIsOpen] = useState(false)
  const [isCreating, setIsCreating] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const normalizedInput = normalize(inputValue)

  const filtered = groups.filter(
    (g) => !selected.some((s) => s.id === g.id) && normalize(g.name).includes(normalizedInput),
  )

  const exactMatch = groups.some((g) => normalize(g.name) === normalizedInput)
  const showCreate = normalizedInput.length > 1 && !exactMatch

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false)
        setInputValue('')
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  function removeGroup(id: string): void {
    onChange(selected.filter((g) => g.id !== id))
  }

  function selectGroup(group: Group): void {
    onChange([...selected, group])
    setInputValue('')
    inputRef.current?.focus()
  }

  async function handleCreate(): Promise<void> {
    if (!inputValue.trim() || isCreating) return
    setIsCreating(true)
    onCreate(inputValue.trim(), (newGroup) => {
      onChange([...selected, newGroup])
      setInputValue('')
      setIsCreating(false)
      inputRef.current?.focus()
    })
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>): void {
    if (e.key === 'Backspace' && !inputValue && selected.length) {
      onChange(selected.slice(0, -1))
    }
    if (e.key === 'Enter') {
      e.preventDefault()
      if (filtered.length === 1) {
        selectGroup(filtered[0])
      } else if (showCreate) {
        handleCreate()
      }
    }
    if (e.key === 'Escape') {
      setIsOpen(false)
      setInputValue('')
    }
  }

  return (
    <Container ref={containerRef}>
      <InputWrapper
        onClick={() => {
          setIsOpen(true)
          inputRef.current?.focus()
        }}>
        {selected.map((g) => (
          <Tag key={g.id}>
            {g.name}
            <TagRemoveBtn
              onClick={(e) => {
                e.stopPropagation()
                removeGroup(g.id)
              }}>
              ×
            </TagRemoveBtn>
          </Tag>
        ))}
        <SearchInput
          ref={inputRef}
          value={inputValue}
          onChange={(e) => {
            setInputValue(e.target.value)
            setIsOpen(true)
          }}
          onFocus={() => setIsOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={selected.length ? '' : 'Adicionar grupo...'}
        />
      </InputWrapper>

      {isOpen && (filtered.length > 0 || showCreate) && (
        <Dropdown>
          {filtered.map((g) => (
            <DropdownItem
              key={g.id}
              onMouseDown={(e) => {
                e.preventDefault()
                selectGroup(g)
              }}>
              <GroupTypeBadge $isGlobal={g.isGlobal}>{g.isGlobal ? 'Global' : 'Custom'}</GroupTypeBadge>
              {g.name}
            </DropdownItem>
          ))}
          {showCreate && (
            <CreateItem
              $isCreating={isCreating}
              $hasBorder={filtered.length > 0}
              onMouseDown={(e) => {
                e.preventDefault()
                handleCreate()
              }}>
              {isCreating ? 'Criando...' : `+ Criar "${inputValue.trim()}"`}
            </CreateItem>
          )}
        </Dropdown>
      )}
    </Container>
  )
})
